import React from 'react';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetSalesInvoicingDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import {
  AttachmentObjectType,
  ProjectEstimatesStatusEnum,
  ProjectEstimatesStatusObj,
} from '../../../common/pokCore/validation/enums';
import numberFormatter from '../../../common/numberFormatter';
import { teamName } from '../../../utils/teamName';

import { ManyAttachmentLink } from './ManyAttachmentLink';
import { MarkerForFinance } from './MarkerForFinance';

interface SalesInvoicingSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh?: boolean;
  additionalFiltersRefresh?: boolean;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
}

export const salesInvoicingForGrid = (
  t: TFunction<'fvsLng', 'fvsLng'>,
  tk: TKeys<'fvsLng'>,
  estimateByMonth: GetSalesInvoicingDto,
  refresh: boolean,
  setRefresh: (refresh: boolean) => void,
) => {
  return {
    key: estimateByMonth.id,
    rowCssClass:
      estimateByMonth.status === ProjectEstimatesStatusEnum.Invoiced
        ? 'blue-row'
        : estimateByMonth.status ===
              ProjectEstimatesStatusEnum.ReadyForInvoice &&
            !estimateByMonth.confirmInDocs
          ? 'orange-row'
          : estimateByMonth.status ===
                ProjectEstimatesStatusEnum.ReadyForInvoice &&
              estimateByMonth.confirmInDocs
            ? 'green-row'
            : estimateByMonth.status === ProjectEstimatesStatusEnum.Withdrawn
              ? 'red-row'
              : '',
    values: {
      'estimateByMonth.date': momencik(estimateByMonth.date, 'YYYY-MM'),
      new_status_ts_for_ready: estimateByMonth.statusTsForReady
        ? momencik(estimateByMonth.statusTsForReady, 'LLL')
        : null,
      'project.name': estimateByMonth.project.name,
      'project.systemNumber': estimateByMonth.project.systemNumber,
      'project.number': estimateByMonth.project.number,
      'profile.name': estimateByMonth.profile.name,
      'responsibleOwner.name': estimateByMonth.project.responsibleOwner?.name,
      'budget.name': estimateByMonth.project.purchaser.budget.name,
      'team.name': estimateByMonth.project.projectTeams
        ?.map(({ team }) => teamName(team))
        ?.join(', '),
      docs_confirm: estimateByMonth.confirmInDocs
        ? t(tk.common.yes)
        : t(tk.common.no),
      has_correction: estimateByMonth.hasCorrection
        ? t(tk.common.yes)
        : t(tk.common.no),
      salesTotal: numberFormatter(estimateByMonth.salesTotal, true),
      amountToInvoice: numberFormatter(estimateByMonth.amountToInvoice, true),
      status_text: t(ProjectEstimatesStatusObj[estimateByMonth.status]),
      commentMediaplanner: (
        <ManyAttachmentLink
          objectId={estimateByMonth.id}
          objectType={AttachmentObjectType.EstimateByMonthMediaplanner}
          note={estimateByMonth.lastCommentMediaplanner}
          notes={estimateByMonth.commentsMediaplanner}
          setRefresh={setRefresh}
          refresh={refresh}
          additionalText={t(tk.attachment.mediaplanner)}
        />
      ),
      commentFinance: (
        <ManyAttachmentLink
          objectId={estimateByMonth.id}
          objectType={AttachmentObjectType.EstimateByMonthFinance}
          note={estimateByMonth.lastCommentFinance}
          notes={estimateByMonth.commentsFinance}
          setRefresh={setRefresh}
          refresh={refresh}
          additionalText={t(tk.attachment.finance)}
          checkFinance={true}
        />
      ),
      markerForFinance: (
        <MarkerForFinance
          objectId={estimateByMonth.id}
          status={
            estimateByMonth.status as unknown as ProjectEstimatesStatusEnum
          }
          setRefresh={setRefresh}
          refresh={refresh}
        />
      ),
    },
    tdCssClass: {
      salesTotal: 'amounts-align-right',
      amountToInvoice: 'amounts-align-right',
    },
  };
};

export const SalesInvoicingSearch: React.FC<
  SalesInvoicingSearchProps
> = props => {
  const { t, tk } = useTranslation('fvsLng');

  const columns: GridColumn[] = [
    { header: t(tk.common.team), property: 'team.name' },
    {
      header: t(tk.common.responsiblePerson),
      property: 'responsibleOwner.name',
    },
    { header: t(tk.common.budget), property: 'budget.name' },
    {
      header: `${t(tk.common.sysNumber)} ${t(tk.prLang.genitiveProject)}`,
      property: 'project.systemNumber',
    },
    {
      header: `${t(tk.common.number)} ${t(tk.prLang.genitiveProject)}`,
      property: 'project.number',
    },
    {
      header: `${t(tk.common.name)} ${t(tk.prLang.genitiveProject)}`,
      property: 'project.name',
    },
    { header: t(tk.common.profile), property: 'profile.name' },
    { header: t(tk.common.month), property: 'estimateByMonth.date' },
    {
      header: t(tk.common.status),
      property: 'status_text',
    },
    {
      header: t(tk.finance.statusInDocs),
      property: 'docs_confirm',
    },
    {
      header: t(tk.finance.hasCorrection),
      property: 'has_correction',
    },
    {
      header: t(tk.finance.salesTotal),
      property: 'salesTotal',
      noFilter: true,
      noSorting: true,
    },
    {
      header: t(tk.finance.amountToInvoiced),
      property: 'amountToInvoice',
      noFilter: true,
      noSorting: true,
    },
    {
      header: t(tk.finance.commentMediaPlanner),
      property: 'commentMediaplanner',
      noFilter: true,
      noSorting: true,
    },
    {
      header: t(tk.finance.commentFinance),
      property: 'commentFinance',
      noFilter: true,
      noSorting: true,
    },
    {
      header: t(tk.finance.changeToReady),
      property: 'new_status_ts_for_ready',
      noFilter: true,
    },
    {
      header: t(tk.finance.markerForFinance),
      property: 'markerForFinance',
      noFilter: true,
      noSorting: true,
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="project.systemNumber"
      initialOrderDirection="ASC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.prLang.noEstimateByMonth)}
      refresh={props.refresh}
      additionalFiltersRefresh={props.additionalFiltersRefresh}
      onRowClick={props.onRowClick}
      withHistory={false}
      withFilterInColumns={true}
      withOverflowXAuto={true}
    />
  );
};
